import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import Logo from '../components/ui/Logo';
import api from '../services/api';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loginMode, setLoginMode] = useState('team');
  const [amazonEmail, setAmazonEmail] = useState('');
  const [sellerNotRegistered, setSellerNotRegistered] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const { login, forgotPassword } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'qc') {
      navigate('/login/qc', { replace: true });
    }
  }, [searchParams, navigate]);

  const modeActive = (mode) => loginMode === mode;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        toast.success('Welcome back!');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Connection failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await forgotPassword(forgotPasswordEmail);
      if (result.success) {
        toast.success(result.message || 'Password reset email sent!');
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSellerLogin = async () => {
    if (!amazonEmail || !amazonEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSellerNotRegistered(false);
    setLoading(true);

    try {
      const clientUrl = encodeURIComponent(window.location.origin);
      const response = await api.get(
        `/onboarding/amazon/start?email=${encodeURIComponent(amazonEmail)}&type=login&clientUrl=${clientUrl}`
      );

      if (response.data.success) {
        const redirectUri = response.data.redirectUri || '';
        if (redirectUri.includes('localhost') || redirectUri.includes('127.0.0.1')) {
          toast.error(
            'Store connect uses localhost — only works on this computer. '
            + 'For a remote seller, deploy to a public URL (or ngrok) and set AMAZON_REDIRECT_URI in server/.env.',
            { duration: 8000 }
          );
        }
        window.location.href = response.data.authUrl;
      } else {
        toast.error(response.data.error || 'Failed to start Amazon login');
        setLoading(false);
      }
    } catch (error) {
      const code = error.response?.data?.code;
      const status = error.response?.status;
      if (code === 'SELLER_NOT_REGISTERED' || status === 404) {
        setSellerNotRegistered(true);
        toast.error('This email is not registered with SellerClub yet.');
      } else {
        toast.error(error.response?.data?.error || error.message || 'Failed to start Amazon login');
      }
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden transition-colors duration-500 ${
      isDarkMode ? 'bg-black' : 'bg-gradient-to-br from-orange-50 via-amber-50 to-stone-100'
    }`}>
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className={`fixed top-6 right-6 z-50 p-3 rounded-xl transition-all duration-300 ${
          isDarkMode 
            ? 'bg-white/10 hover:bg-white/20 border border-white/10' 
            : 'bg-white hover:bg-slate-50 border border-slate-200 shadow-lg'
        }`}
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      {/* Background */}
      <div className="absolute inset-0">
        {isDarkMode ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-black"></div>
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full filter blur-3xl"></div>
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(148 163 184 / 0.05) 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </>
        ) : (
          <>
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-200/40 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-200/40 rounded-full filter blur-3xl"></div>
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(148 163 184 / 0.08) 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center py-16 px-6">
        <div className="max-w-7xl w-full">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            
            {/* Left Branding */}
            <div className="lg:col-span-2 space-y-12 hidden lg:block">
              <div className="space-y-8">
                <div className="relative inline-block">
                  <div className={`absolute -inset-3 bg-gradient-to-r rounded-2xl blur-xl ${
                    isDarkMode ? 'from-orange-500/20 to-stone-1000/20' : 'from-orange-300/30 to-amber-300/30'
                  }`}></div>
                  <Logo className="relative" size="xl" isDarkMode={isDarkMode} />
                </div>
                
                <div className="space-y-4">
                  <h1 className="text-7xl font-bold tracking-tight leading-none">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-stone-1000">
                      Next-Gen
                    </span>
                    <br/>
                    <span className={`font-light ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      Amazon Ads
                    </span>
                  </h1>
                  
                  <p className={`text-xl leading-relaxed font-normal max-w-lg ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-600'
                  }`}>
                    Intelligent automation meets enterprise-grade analytics.
                    Transform your Amazon advertising with AI.
                  </p>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4">
                {[
                  { 
                    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                    title: 'Instant Onboarding', 
                    desc: 'Connect unlimited stores in seconds',
                    color: 'orange'
                  },
                  { 
                    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
                    title: 'AI Optimization', 
                    desc: 'Smart campaign management automation',
                    color: 'purple'
                  },
                  { 
                    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
                    title: 'Real-Time Analytics', 
                    desc: 'Live performance insights & reporting',
                    color: 'emerald'
                  },
                  { 
                    icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
                    title: 'Enterprise Security', 
                    desc: 'Bank-level encryption & compliance',
                    color: 'violet'
                  }
                ].map((feature, idx) => {
                  const getIconColors = (color) => {
                    if (isDarkMode) {
                      const darkColors = {
                        orange: 'bg-orange-500/10 border-orange-500/25 text-orange-400',
                        purple: 'bg-orange-500/10 border-amber-500/20 text-amber-300',
                        emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
                        violet: 'bg-stone-800/80 border-orange-500/20 text-orange-300',
                      };
                      return darkColors[color] || darkColors.orange;
                    }
                    const lightColors = {
                      orange: 'bg-orange-100 border-orange-200 text-orange-700',
                      purple: 'bg-amber-50 border-amber-200 text-amber-800',
                      emerald: 'bg-emerald-100 border-emerald-200 text-emerald-700',
                      violet: 'bg-stone-100 border-orange-200 text-stone-800',
                    };
                    return lightColors[color] || lightColors.orange;
                  };
                  
                  return (
                    <div key={idx} className={`flex items-start space-x-4 p-5 rounded-2xl border transition-all duration-300 group ${
                      isDarkMode 
                        ? 'bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.04] hover:border-white/10'
                        : 'bg-white/60 border-slate-200/50 hover:bg-white hover:border-slate-300 shadow-sm'
                    }`}>
                      <div className={`flex-shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center transition-all ${getIconColors(feature.color)}`}>
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className={`font-semibold text-base mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {feature.title}
                        </h3>
                        <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
          </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4">
                {[
                  { label: 'Stores', value: '1000+' },
                  { label: 'Campaigns', value: '50K+' },
                  { label: 'Uptime', value: '99.9%' }
                ].map((stat, idx) => (
                  <div key={idx} className={`text-center p-4 rounded-xl border ${
                    isDarkMode 
                      ? 'bg-white/[0.02] border-white/[0.05]'
                      : 'bg-white/60 border-slate-200/50'
                  }`}>
                    <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-stone-1000">{stat.value}</div>
                    <div className={`text-xs mt-1 font-medium uppercase tracking-wider ${
                      isDarkMode ? 'text-slate-600' : 'text-slate-500'
                    }`}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Login Panel */}
            <div className="lg:col-span-3 w-full max-w-[27rem] mx-auto">
              <div className="relative">
                <div className={`absolute -inset-1 rounded-3xl blur-xl ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-orange-500/20'
                    : 'bg-gradient-to-r from-orange-200/30 via-amber-200/30 to-orange-200/30'
                }`}></div>
                
                <div className={`relative rounded-3xl border shadow-2xl overflow-hidden surface-card ${
                  isDarkMode 
                    ? 'border-slate-700'
                    : 'border-slate-200'
                }`}>
                  <div className="h-px bg-gradient-to-r from-transparent via-orange-500 to-transparent"></div>
                  
                  <div className="p-7">
                    <div className="text-center mb-7">
                      <h2 className={`text-2xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Welcome Back
                      </h2>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                        Admin &amp; team, or Amazon seller login.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-7">
                      {[
                        { id: 'team', title: 'Admin & team', hint: 'Work email' },
                        { id: 'amazon', title: 'Amazon', hint: 'Seller login' },
                      ].map(({ id, title, hint }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setLoginMode(id)}
                          className={`relative p-3.5 rounded-xl transition-all duration-200 ${
                            modeActive(id)
                              ? isDarkMode
                                ? 'bg-slate-800 border border-orange-500/30 shadow-lg'
                                : 'bg-gradient-to-br from-orange-500 to-orange-700 border border-orange-600 shadow-xl'
                              : isDarkMode
                                ? 'bg-slate-800/30 border border-white/5 hover:border-white/10'
                                : 'bg-slate-100 border border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div
                            className={`font-semibold text-sm ${
                              modeActive(id) ? 'text-white' : isDarkMode ? 'text-slate-400' : 'text-slate-700'
                            }`}
                          >
                            {title}
                          </div>
                          <div
                            className={`text-xs mt-0.5 ${
                              modeActive(id)
                                ? isDarkMode
                                  ? 'text-orange-400'
                                  : 'text-orange-100'
                                : isDarkMode
                                  ? 'text-slate-600'
                                  : 'text-slate-500'
                            }`}
                          >
                            {hint}
                          </div>
                        </button>
                      ))}
                    </div>

                    <div>
                    {loginMode === 'team' ? (
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div className="space-y-5">
                <div>
                            <label className={`block text-sm font-medium mb-2.5 ${
                              isDarkMode ? 'text-slate-400' : 'text-slate-700'
                            }`}>
                    Work email
                  </label>
                            <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                                className={`w-full px-4 py-3.5 pl-12 rounded-xl transition-all duration-200 ${
                                  isDarkMode
                                    ? 'bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-600 focus:bg-slate-800/80 focus:border-orange-500/50'
                                    : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20'
                                }`}
                                placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                              <svg className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                                isDarkMode ? 'text-slate-600' : 'text-slate-400'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                              </svg>
                            </div>
                </div>

                <div>
                            <label className={`block text-sm font-medium mb-2.5 ${
                              isDarkMode ? 'text-slate-400' : 'text-slate-700'
                            }`}>
                    Password
                  </label>
                            <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                                className={`w-full px-4 py-3.5 pl-12 rounded-xl transition-all duration-200 ${
                                  isDarkMode
                                    ? 'bg-slate-800/50 border border-slate-700/50 text-white placeholder-slate-600 focus:bg-slate-800/80 focus:border-orange-500/50'
                                    : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20'
                                }`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                              <svg className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                                isDarkMode ? 'text-slate-600' : 'text-slate-400'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </div>
                </div>
              </div>

                        <div className="flex items-center justify-between text-sm pt-2">
                          <label className={`flex items-center cursor-pointer transition-colors ${
                            isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-600 hover:text-slate-800'
                          }`}>
                            <input type="checkbox" className={`w-4 h-4 rounded text-orange-500 focus:ring-orange-500/50 focus:ring-offset-0 ${
                              isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-300 bg-white'
                            }`} />
                            <span className="ml-2">Remember me</span>
                    </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      if (formData.email && !forgotPasswordEmail) {
                        setForgotPasswordEmail(formData.email.trim());
                      }
                    }}
                            className="font-medium text-orange-500 hover:text-orange-400 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

              <button
                type="submit"
                disabled={loading}
                          className="relative w-full group mt-8"
              >
                          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-700 rounded-xl opacity-90 group-hover:opacity-100 transition-opacity"></div>
                          <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                          <div className="relative px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-700 rounded-xl font-semibold text-white text-base flex items-center justify-center shadow-lg">
                {loading ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                Authenticating...
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                </svg>
                                Access Dashboard
                              </>
                            )}
                          </div>
              </button>

                        <p className="text-center text-xs text-slate-600 pt-2">
                          Secure access for admins & team members
                        </p>
            </form>
                    ) : (
                      <div className="space-y-6">
                        <div className="text-center space-y-3 mb-2">
                          <div
                            className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl border ${
                              isDarkMode
                                ? 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 border-orange-500/20'
                                : 'bg-gradient-to-br from-orange-100 to-amber-100 border-orange-200'
                            }`}
                          >
                            <svg
                              className={`w-8 h-8 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.654-1.244.743-2.64 1.316-4.185 1.726-1.53.406-3.045.608-4.516.608-3.816 0-7.226-.955-10.21-2.86-.498-.314-.862-.626-1.07-.932-.197-.29-.194-.527.024-.733l.285-.352z" />
                            </svg>
                          </div>
                          <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                            Use your SellerClub registered email, then sign in with Amazon.
                          </p>
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-2.5 ${
                              isDarkMode ? 'text-slate-400' : 'text-slate-700'
                            }`}
                          >
                            SellerClub registered email
                          </label>
                          <div className="relative">
                            <input
                              id="amazon-email"
                              type="email"
                              className={`w-full px-4 py-3.5 pl-12 rounded-xl transition-all duration-200 ${
                                isDarkMode
                                  ? 'bg-slate-800/80 border-2 border-orange-500/30 text-white placeholder-slate-500 focus:border-orange-500/60'
                                  : 'bg-orange-50/50 border-2 border-orange-200 text-slate-900 placeholder-slate-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/25'
                              }`}
                              placeholder="Same email you used when connecting stores"
                              value={amazonEmail}
                              onChange={(e) => {
                                setAmazonEmail(e.target.value);
                                setSellerNotRegistered(false);
                              }}
                            />
                            <svg
                              className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                                isDarkMode ? 'text-slate-600' : 'text-slate-400'
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                              />
                            </svg>
                          </div>
                        </div>

                        {sellerNotRegistered && (
                          <div
                            className={`rounded-xl border px-4 py-3 text-sm ${
                              isDarkMode
                                ? 'bg-amber-950/40 border-amber-500/30 text-amber-100'
                                : 'bg-amber-50 border-amber-200 text-amber-950'
                            }`}
                          >
                            <p className="font-semibold">You haven&apos;t signed up yet</p>
                            <Link
                              to="/seller/signup"
                              className="inline-block mt-2 font-semibold text-orange-600 hover:text-orange-700 underline"
                            >
                              Sign up →
                            </Link>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={handleSellerLogin}
                          disabled={loading}
                          className="relative w-full group mt-2"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl opacity-90 group-hover:opacity-100 transition-opacity" />
                          <div className="relative px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl font-semibold text-white text-base flex items-center justify-center shadow-lg">
                            {loading ? 'Connecting…' : 'Login with Amazon'}
                          </div>
                        </button>

                        <p className="text-center text-sm text-slate-600 dark:text-slate-400">
                          New to SellerClub?{' '}
                          <Link to="/seller/signup" className="font-semibold text-orange-600 hover:text-orange-700">
                            Sign up
                          </Link>
                        </p>
                      </div>
                    )}

                    <p
                      className={`text-center text-sm mt-6 pt-4 border-t ${
                        isDarkMode ? 'border-slate-700 text-slate-500' : 'border-slate-100 text-slate-600'
                      }`}
                    >
                      Quick Commerce store portal?{' '}
                      <Link to="/login/qc" className="font-semibold text-orange-600 hover:text-orange-700">
                        Sign in with OTP →
                      </Link>
                    </p>

            {showForgotPassword && (
                        <div
                          className={`mt-6 p-5 rounded-xl border ${
                            isDarkMode
                              ? 'bg-slate-800/80 border-slate-600'
                              : 'bg-orange-50/90 border-orange-200'
                          }`}
                        >
                          <div className="flex items-center mb-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                              isDarkMode ? 'bg-orange-500/25' : 'bg-orange-100'
                            }`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                  </div>
                  <div className="ml-3 min-w-0">
                              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Reset password</h3>
                              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                We&apos;ll email a secure link if this account exists
                              </p>
                  </div>
                </div>
                <form onSubmit={handleForgotPassword} className="space-y-3">
                    <input
                      type="email"
                      required
                      autoComplete="email"
                              className={`w-full px-4 py-3 rounded-xl border transition-all ${
                                isDarkMode
                                  ? 'bg-slate-900 border-slate-600 text-white placeholder-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30'
                                  : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20'
                              }`}
                      placeholder="Work email address"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    />
                            <div className="grid grid-cols-2 gap-2.5">
                    <button
                      type="submit"
                      disabled={loading}
                                className="px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium disabled:opacity-50 transition-all"
                    >
                                {loading ? 'Sending…' : 'Send link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail('');
                      }}
                                className={`px-4 py-3 rounded-xl font-medium transition-all ${
                                  isDarkMode
                                    ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                    : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
                                }`}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className={`text-sm ${isDarkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                  Protected by enterprise-grade security
                </p>
                <div className={`flex items-center justify-center space-x-4 mt-3 text-xs ${
                  isDarkMode ? 'text-slate-700' : 'text-slate-500'
                }`}>
                  <span>256-bit SSL</span>
                  <span>•</span>
                  <span>SOC 2 Type II</span>
                  <span>•</span>
                  <span>GDPR Compliant</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
